import { isApiError, problemDetailText } from '../../services/apiError'
import { NOT_MEASURED, agoLabel, errorKind, errorSummary, exactNumber, whenLabel } from '../../utils/format'
import type { AdminGate } from '../../utils/adminGate'
import type { QueryLike } from '../overview/overviewFormat'
import { signInRequiredText } from '../jobs/haltClearFormat'
import { HANDLING_MIGRATION, SIGNALS_MIGRATION, activityText, resolutionText } from './signalFormat'
import type { InboxFilters, SignalLink } from './signalFormat'
import type { SignalHandlingVerb } from '../../services/signalsApi'
import type {
  Signal, SignalActivity, SignalAssignee, SignalAssigneeList, SignalHandling, SignalHandlingAct,
  SignalHandlingResult, SignalList, SignalPreviousOccurrence, SignalSummary,
} from '../../types/signals'

/**
 * The people side of a signal — acknowledge ("I have got this"), assign, release (D6b) — as pure decisions: what each
 * handling state says, which controls to offer, how the detector's and the people's timelines interleave, what a
 * refusal means, and what the Inbox's count line says.
 *
 * Pure and vitest-safe: types, utils, services/apiError and other pure format modules. Nothing here imports
 * services/api.ts (MSAL reads `window` at load) or a hook. The verb TYPE is imported from services/signalsApi as a
 * type only, which is erased.
 *
 * THREE RULES run through it.
 *  1. Handling hides nothing. An acknowledged or assigned signal is still OPEN and still counted: every count here
 *     is a part said beside its whole ("12 open · 3 acknowledged"), never subtracted from it.
 *  2. Unknown is not unowned. `handling: null` (the table is missing here, or an older API) is NOT_MEASURED with
 *     its reason; "Unowned" is a measured state and is said as plain text, not as a dash.
 *  3. A write fails closed. Controls render only on the handling gate's `admin` state, only on an open signal whose
 *     handling was served, and a person is named only by the user id the assignee list served.
 */

// ---------------------------------------------------------------------------
// Fixed copy
// ---------------------------------------------------------------------------

/** Where handling was not served: the table does not exist in this database, or the API predates it. */
export const HANDLING_NOT_RECORDED = 'handling is not recorded in this database'

/** Where a recorded name was withheld from a caller who does not pass LighthouseAdmin. */
export const NAMES_WITHHELD = 'name shown to administrators'

/** A name the audit trail recorded no display name for. Never a dash: somebody acted. */
export const UNNAMED_PERSON = 'an unnamed user'

/** The assign dialog's standing caveat. */
export const ASSIGNEE_NOT_NOTIFIED =
  'The assignee is not notified: Observatory sends nothing. Tell them yourself.'

/** Why the assignee list is short, and what else there is: the server reads the audit trail, not a directory. */
export function noDirectoryText(windowDays: number): string {
  return 'There is no directory to search. This lists the people who acted as Lighthouse administrators here — '
    + `performed an Observatory write — in the last ${exactNumber(windowDays)} days. Anyone else who can handle `
    + 'signals can take this one themselves with “I have got this”.'
}

/** What every act leaves true, said in the panel so "acknowledged" is never read as "resolved". */
export const HANDLING_KEEPS_OPEN =
  'Handling never closes a signal: acknowledged or assigned, it stays open and counted until the detector sees the '
  + 'condition clear. A re-raise is a new signal and starts unowned.'

/** The handling gate said no, without a reason. */
export const HANDLING_REFUSED_FALLBACK = 'Handling a signal needs an Entra sign-in holding Lighthouse.Admin.'

/**
 * The rules whose observed value embeds a counter that grows on every pass (SignalHandlingRules.ObservedValueChurns):
 * "no heartbeat for N min", "N consecutive failure(s)". The server does not count their value moving as a change, so
 * the changed-since sentence never cites the value for them either. Compared ignoring case, as the server does.
 */
export const OBSERVED_VALUE_CHURNS: readonly string[] = ['etl-run-stale', 'source-unreachable']

function comparesObservedValue(ruleId: string): boolean {
  const id = ruleId.trim().toLowerCase()
  return !OBSERVED_VALUE_CHURNS.includes(id)
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

/** A served display name, or null when none is recorded or it was withheld. */
function served(name: string | null | undefined): string | null {
  return name?.trim() ? name.trim() : null
}

/**
 * The owner as the sentence names them: "you" when the server says the caller owns it, the stamped name, null when
 * the name was withheld (the sentence then drops the name and says why), or "an unnamed user".
 */
function ownerSlot(h: Pick<SignalHandling, 'ownedByCaller' | 'ownerName' | 'namesWithheld'>): string | null {
  if (h.ownedByCaller) return 'you'
  return served(h.ownerName) ?? (h.namesWithheld ? null : UNNAMED_PERSON)
}

function nameSlot(name: string | null | undefined, withheld: boolean): string | null {
  return served(name) ?? (withheld ? null : UNNAMED_PERSON)
}

// ---------------------------------------------------------------------------
// One signal's handling
// ---------------------------------------------------------------------------

export type HandlingKind = 'unrecorded' | 'unowned' | 'assigned' | 'acknowledged' | 'unknown'

export interface HandlingView {
  kind: HandlingKind
  /** The one line: "Unowned", "Assigned to Bob · not acknowledged", "Acknowledged by Alice · 5m ago". */
  text: string
  /** Who did the latest act when it was not the owner's own ("assigned by Carol · 5m ago"); else null. */
  byline: string | null
  /** {@link NAMES_WITHHELD} when the server withheld a name from this caller; else null. */
  withheld: string | null
  /** Why `text` is a dash ({@link HANDLING_NOT_RECORDED}); else null. */
  note: string | null
  /** The condition moved after the act: "acknowledged when X, now Y — changed since". Else null. */
  changed: string | null
  /** The signal is resolved: this is its final, frozen handling, said in the past tense. */
  final: boolean
}

export type HandlingSubject = Pick<Signal, 'isOpen' | 'ruleId' | 'observedValue' | 'conditionSince' | 'handling'>

/**
 * What a signal's handling says, for the table, the drawer and the preview.
 *
 * - `handling` null or absent: NOT_MEASURED, "handling is not recorded in this database" — unknown, never unowned.
 * - Unowned: plain "Unowned" (released ones say who let go); Assigned: "Assigned to Bob · not acknowledged";
 *   Acknowledged: "Acknowledged by Alice · 5m ago", "you" when the server says the caller owns it.
 * - A withheld name is dropped from the sentence and `withheld` says "name shown to administrators".
 * - A resolved signal's handling is final, and said in the past tense.
 * - `changed` only when the server says `changedSinceHandled`.
 */
export function handlingView(s: HandlingSubject): HandlingView {
  const h = s.handling
  const final = !s.isOpen
  const base = { byline: null, withheld: null, note: null, changed: null, final }
  if (h == null) {
    return { ...base, kind: 'unrecorded', text: NOT_MEASURED, note: HANDLING_NOT_RECORDED }
  }

  const withheld = h.namesWithheld ? NAMES_WITHHELD : null
  const ago = agoLabel(h.since)
  const by = nameSlot(h.byName, h.namesWithheld)
  const owner = ownerSlot(h)

  switch (h.state) {
    case 'Unowned': {
      const byline = h.lastAction === 'Released'
        ? (by ? `released by ${by} · ${ago}` : `released · ${ago}`)
        : null
      return { ...base, kind: 'unowned', text: final ? 'Unowned when it ended' : 'Unowned', byline, withheld }
    }
    case 'Assigned': {
      const text = final
        ? (owner ? `Was assigned to ${owner} · never acknowledged` : 'Was assigned · never acknowledged')
        : (owner ? `Assigned to ${owner} · not acknowledged` : 'Assigned · not acknowledged')
      const byline = by ? `assigned by ${by} · ${ago}` : `assigned · ${ago}`
      return { ...base, kind: 'assigned', text, byline, withheld, changed: changedText(s, h, 'assigned') }
    }
    case 'Acknowledged': {
      const who = owner ? ` by ${owner}` : ''
      const text = final ? `Was acknowledged${who} · ${ago}` : `Acknowledged${who} · ${ago}`
      return { ...base, kind: 'acknowledged', text, withheld, changed: changedText(s, h, 'acknowledged') }
    }
    default:
      // A state this build does not know: its raw name, never a guessed meaning.
      return { ...base, kind: 'unknown', text: String(h.state), withheld }
  }
}

/**
 * The changed-since sentence, or null. Cites the value only when the rule's value is compared (not a churning
 * counter) and it differs; else the condition's start, else the re-observation — the server's three tests, in order.
 */
function changedText(s: HandlingSubject, h: SignalHandling, verb: 'acknowledged' | 'assigned'): string | null {
  if (!h.changedSinceHandled) return null
  const then = h.whenHandled
  const now = s.isOpen ? 'now' : 'last'
  if (comparesObservedValue(s.ruleId) && (then?.observedValue ?? null) !== (s.observedValue ?? null)) {
    return `${verb} when ${then?.observedValue ?? 'no value was recorded'}, ${now} ${s.observedValue ?? 'no value'} — changed since`
  }
  if (then && (then.conditionSince ?? null) !== (s.conditionSince ?? null)) {
    return `the condition's recorded start moved after it was ${verb} — changed since`
  }
  return `the detector lost sight of it after it was ${verb}, then saw it again — changed since`
}

export interface HandlingChip {
  text: string
  /** `unmeasured` draws dimmed; `changed` draws in the warning colour; `plain` is ordinary text. */
  tone: 'unmeasured' | 'plain' | 'changed'
  /** The tooltip: the full sentence, or why there is no answer. */
  title: string
}

/** The short form, for a table cell or a preview row: "Unowned", "Acknowledged · Alice", "Assigned · you · changed since". */
export function handlingChip(s: HandlingSubject): HandlingChip {
  const view = handlingView(s)
  const h = s.handling
  if (view.kind === 'unrecorded' || h == null) {
    return { text: NOT_MEASURED, tone: 'unmeasured', title: HANDLING_NOT_RECORDED }
  }
  const owner = ownerSlot(h)
  const title = [view.text, view.byline, view.withheld, view.changed].filter(Boolean).join(' · ')
  let text: string
  switch (view.kind) {
    case 'unowned': text = 'Unowned'; break
    case 'assigned': text = owner ? `Assigned · ${owner}` : 'Assigned'; break
    case 'acknowledged': text = owner ? `Acknowledged · ${owner}` : 'Acknowledged'; break
    default: text = view.text
  }
  if (view.changed) return { text: `${text} · changed since`, tone: 'changed', title }
  return { text, tone: 'plain', title }
}

// ---------------------------------------------------------------------------
// The controls
// ---------------------------------------------------------------------------

export interface HandlingControls {
  /** "I have got this" — also how a signal is taken over from someone else. */
  acknowledge: boolean
  /** "Assign…" */
  assign: boolean
  /** "Release" */
  release: boolean
  /** On a `denied` gate and an open signal: why there are no controls. Null otherwise. */
  deniedReason: string | null
}

const NO_CONTROLS: HandlingControls = { acknowledge: false, assign: false, release: false, deniedReason: null }

/**
 * Which handling controls to offer. ONLY on the gate's `admin` state, ONLY on an open signal, and only when its
 * handling was served in a state this build knows (no handling table → every act 503s; an unknown state → nothing
 * guessed). "I have got this" is withheld only when the caller already acknowledged it; Release only when somebody
 * has it. `pending` and `unknown` show nothing at all; `denied` says why, on an open signal.
 */
export function handlingControls(gate: AdminGate, s: Pick<Signal, 'isOpen' | 'handling'>): HandlingControls {
  if (gate.state !== 'admin') {
    return gate.state === 'denied' && s.isOpen
      ? { ...NO_CONTROLS, deniedReason: gate.reason.trim() || HANDLING_REFUSED_FALLBACK }
      : NO_CONTROLS
  }
  const h = s.handling
  if (!s.isOpen || h == null) return NO_CONTROLS
  if (h.state !== 'Unowned' && h.state !== 'Assigned' && h.state !== 'Acknowledged') return NO_CONTROLS
  return {
    acknowledge: !(h.state === 'Acknowledged' && h.ownedByCaller),
    assign: true,
    release: h.state !== 'Unowned',
    deniedReason: null,
  }
}

// ---------------------------------------------------------------------------
// The merged timeline
// ---------------------------------------------------------------------------

export interface TimelineEntry {
  key: string
  /** `detector`: a transition Automaton recorded. `person`: a handling act. */
  source: 'detector' | 'person'
  at: string
  text: string
  detail: string | null
}

function actText(act: SignalHandlingAct): string {
  const by = nameSlot(act.byName, act.namesWithheld)
  const owner = nameSlot(act.ownerName, act.namesWithheld)
  const withheld = act.namesWithheld && (!by || !owner) ? ` · ${NAMES_WITHHELD}` : ''
  switch (act.action) {
    case 'Acknowledged':
      return by ? `Acknowledged by ${by} — “I have got this”${withheld}` : `Acknowledged — “I have got this”${withheld}`
    case 'Assigned':
      if (by && owner) return `Assigned to ${owner} by ${by}`
      return `Assigned${owner ? ` to ${owner}` : ''}${by ? ` by ${by}` : ''}${withheld}`
    case 'Released':
      return by ? `Released by ${by}${withheld}` : `Released${withheld}`
    default:
      return String(act.action)
  }
}

function time(iso: string): number {
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
}

/**
 * The detector's transitions and the people's acts in one list, by time. On a tie the detector comes first — the
 * act was taken on what the detector had just said — and within one source the served order is kept (activity is
 * chronological, the history is in revision order). An unparseable instant sorts last rather than throwing.
 */
export function mergedTimeline(
  s: Pick<Signal, 'subjectKind'> & { activity: readonly SignalActivity[]; handlingHistory?: readonly SignalHandlingAct[] | null },
): TimelineEntry[] {
  const detector = s.activity.map((a, index) => ({
    order: 0, index,
    entry: {
      key: `d-${index}-${a.at}`,
      source: 'detector' as const,
      at: a.at,
      text: activityText(a, s.subjectKind),
      detail: [a.host ? `by ${a.host}` : null, a.observedValue ? `observed ${a.observedValue}` : null]
        .filter(Boolean).join(' · ') || null,
    },
  }))
  const people = (s.handlingHistory ?? []).map((act, index) => ({
    order: 1, index,
    entry: {
      key: `p-${act.revision}-${index}`,
      source: 'person' as const,
      at: act.at,
      text: actText(act),
      detail: [`revision ${act.revision}`, act.whenHandled?.observedValue ? `signal said ${act.whenHandled.observedValue}` : null]
        .filter(Boolean).join(' · '),
    },
  }))
  return [...detector, ...people]
    .sort((a, b) => time(a.entry.at) - time(b.entry.at) || a.order - b.order || a.index - b.index)
    .map((x) => x.entry)
}

// ---------------------------------------------------------------------------
// The previous occurrence
// ---------------------------------------------------------------------------

/** "Raised before: the previous signal ended 2d ago (Cleared), acknowledged by Alice. A re-raise starts unowned." */
export function previousOccurrenceText(prev: SignalPreviousOccurrence): string {
  const ended = prev.resolvedAt ? `ended ${agoLabel(prev.resolvedAt)}` : 'ended'
  const how = prev.resolution ? ` (${resolutionText(prev.resolution)})` : ''
  const owner = served(prev.finalOwnerName)
  const named = (verb: string) =>
    owner ? `${verb} ${owner}` : prev.namesWithheld ? `${verb.split(' ')[0]} — ${NAMES_WITHHELD}` : `${verb} ${UNNAMED_PERSON}`
  let handled: string
  switch (prev.finalState) {
    case null: handled = `how it was handled is not recorded (${HANDLING_NOT_RECORDED})`; break
    case 'Unowned': handled = 'with nobody holding it'; break
    case 'Assigned': handled = `${named('assigned to')}, never acknowledged`; break
    case 'Acknowledged': handled = named('acknowledged by'); break
    default: handled = `handled as ${String(prev.finalState)}`
  }
  return `Raised before: the previous signal ${ended}${how}, ${handled}. A re-raise starts unowned.`
}

/**
 * Who the one-click "Assign to X" would hand the signal to: the previous occurrence's final owner, but ONLY when the
 * assignee list holds exactly one person by that exact name — the list's user id is what is sent, so a name shared
 * by two people, or a person the list does not know, offers nothing (the Assign dialog still does). Never when the
 * name was withheld, when nobody had it, or when the current signal is already theirs.
 */
export function oneClickAssignee(
  prev: SignalPreviousOccurrence | null | undefined,
  assignees: readonly SignalAssignee[] | undefined,
  current: SignalHandling | null | undefined,
): SignalAssignee | null {
  if (!prev || !assignees || current == null) return null
  if (prev.finalState !== 'Assigned' && prev.finalState !== 'Acknowledged') return null
  const name = served(prev.finalOwnerName)
  if (!name) return null
  if (current.state !== 'Unowned' && served(current.ownerName) === name) return null
  const matches = assignees.filter((a) => served(a.name) === name)
  return matches.length === 1 ? matches[0] : null
}

// ---------------------------------------------------------------------------
// Notes on what handling does NOT do
// ---------------------------------------------------------------------------

export interface HandlingSubjectNote {
  text: string
  link: SignalLink
}

/**
 * The two subjects whose own acknowledgement lives elsewhere, so acknowledging the SIGNAL is easily mistaken for it:
 * a halt (cleared in the Scheduler) and a critical Data Comb finding (acknowledged on the Data Comb page — which DOES
 * resolve the signal, Suppressed at source). Null for every other subject.
 */
export function handlingSubjectNote(s: Pick<Signal, 'ruleId' | 'subjectKind' | 'subjectRefId'>): HandlingSubjectNote | null {
  const rule = s.ruleId.trim().toLowerCase()
  if (s.subjectKind === 'EtlHalt' || rule === 'etl-halt-active') {
    return {
      text: 'Acknowledging this signal does not clear the halt: runs stay blocked until an administrator clears it '
        + 'in the Scheduler.',
      link: { to: '/jobs', label: 'Open the Scheduler' },
    }
  }
  if (s.subjectKind === 'DataCombFinding' || rule === 'comb-critical-finding') {
    const ref = s.subjectRefId?.trim() ? encodeURIComponent(s.subjectRefId.trim()) : null
    return {
      text: 'Acknowledging this signal does not acknowledge the finding; acknowledging the finding on the Data Comb '
        + 'page resolves this signal and suppresses that finding.',
      link: ref ? { to: `/data-comb?finding=${ref}`, label: 'Open the finding' } : { to: '/data-comb', label: 'Open the Data Comb' },
    }
  }
  return null
}

/** Where the audit trail shows every act on this signal: its rows carry `entityType` OperationalSignal, `entityId` the signal. */
export function handlingAuditLink(signalId: string): string {
  return `/audit?${new URLSearchParams({ entityId: signalId }).toString()}`
}

// ---------------------------------------------------------------------------
// Refusals
// ---------------------------------------------------------------------------

export type HandlingErrorKind =
  | 'changed' | 'resolved' | 'unknown-assignee' | 'use-acknowledge' | 'bad-request' | 'sign-in' | 'forbidden'
  | 'not-found' | 'unprovisioned' | 'failed'

export interface HandlingErrorView {
  kind: HandlingErrorKind
  title: string
  message: string
  /** `changed` only: the handling as it stands now, and the revision "Take it over" re-sends. */
  current: SignalHandling | null
  freshRevision: number | null
}

function bodyRecord(error: unknown): Record<string, unknown> | null {
  if (!isApiError(error)) return null
  const body = error.body
  return typeof body === 'object' && body !== null && !Array.isArray(body) ? (body as Record<string, unknown>) : null
}

function isHandling(value: unknown): value is SignalHandling {
  return typeof value === 'object' && value !== null && typeof (value as SignalHandling).revision === 'number'
    && typeof (value as SignalHandling).state === 'string'
}

/** One line for a handling as it stands — the HandlingChanged refusal's "who changed it". */
export function describeHandling(h: SignalHandling): string {
  const view = handlingView({ isOpen: true, ruleId: '', observedValue: null, conditionSince: null, handling: { ...h, changedSinceHandled: false } })
  return [view.text, view.byline, view.withheld].filter(Boolean).join(' · ')
}

/**
 * Why an act failed, by the refusal's `code` first and then its status. Every sentence ends with what is still true —
 * nothing was changed, or it is unknown whether it was.
 *
 * - 409 `HandlingChanged`: who acted first, with the current handling and the revision a "Take it over" re-sends.
 * - 409 `SignalResolved`: "resolved at T" — the handling is final.
 * - 400 `UnknownAssignee`, `UseAcknowledge`, or the server's own words.
 * - 401 / not sent: an Entra sign-in is required (the shared API key is refused even locally).
 * - 403: the server's problem detail first (the "no identifier" refusal names itself), else the missing role.
 * - 404; 503 naming the missing migration; 5xx / network: unknown whether it was recorded, and retrying is safe —
 *   the same act repeated answers `changed: false`.
 */
export function handlingErrorView(error: unknown, entraConfigured = true): HandlingErrorView {
  const none = { current: null, freshRevision: null }
  const body = bodyRecord(error)
  const code = typeof body?.code === 'string' ? body.code : null
  const detail = isApiError(error) ? problemDetailText(error.body) : null

  const signIn = signInRequiredText(error, entraConfigured)
  if (signIn) {
    return { ...none, kind: 'sign-in', title: errorKind(error) === 'sign-in-required' ? 'Not sent' : 'Refused (401)', message: signIn }
  }

  if (code === 'HandlingChanged' || (errorKind(error) === 'conflict' && isHandling(body?.handling))) {
    const current = isHandling(body?.handling) ? body.handling : null
    return {
      kind: 'changed',
      title: 'Somebody acted on this signal first',
      message: current
        ? `Since you read it, its handling changed: ${describeHandling(current)}. Nothing was changed.`
        : 'Since you read it, somebody acted on this signal. Nothing was changed.',
      current,
      freshRevision: current?.revision ?? null,
    }
  }

  if (code === 'SignalResolved') {
    const at = typeof body?.resolvedAt === 'string' ? body.resolvedAt : null
    const resolution = typeof body?.resolution === 'string' ? body.resolution : null
    return {
      ...none,
      kind: 'resolved',
      title: 'The detector already resolved this signal',
      message: `Resolved at ${whenLabel(at)}${resolution ? ` (${resolutionText(resolution)})` : ''}, so its handling is `
        + 'final. If the condition returns it is raised as a new signal. Nothing was changed.',
    }
  }

  if (code === 'UnknownAssignee') {
    return {
      ...none,
      kind: 'unknown-assignee',
      title: 'Not someone Observatory can name',
      message: 'That person has not performed an Observatory write recently, so nobody is known by that id to hand the '
        + 'signal to. Pick someone from the list, or let them take it themselves. Nothing was changed.',
    }
  }

  if (code === 'UseAcknowledge') {
    return {
      ...none,
      kind: 'use-acknowledge',
      title: 'Use “I have got this”',
      message: 'Assigning a signal to yourself is acknowledging it. Nothing was changed.',
    }
  }

  switch (errorKind(error)) {
    case 'bad-request':
      return { ...none, kind: 'bad-request', title: 'Refused (400)', message: detail ?? errorSummary(error) ?? 'The server refused the request. Nothing was changed.' }
    case 'forbidden':
      return {
        ...none,
        kind: 'forbidden',
        title: 'Refused (403)',
        message: detail ?? 'Handling a signal requires the Lighthouse.Admin app role, which this account does not hold. Nothing was changed.',
      }
    case 'not-found':
      return {
        ...none,
        kind: 'not-found',
        title: 'No such signal',
        message: 'Nothing in the store has this id: it was trimmed by retention, or never existed. Nothing was changed.',
      }
    case 'conflict':
      return { ...none, kind: 'changed', title: 'Conflict (409)', message: `${detail ?? 'The server refused the act as a conflict.'} Nothing was changed.` }
    case 'server': {
      if (isApiError(error) && error.status === 503) {
        const said = `${error.title ?? ''} ${error.detail ?? ''} ${error.message}`
        const migration = said.includes(HANDLING_MIGRATION) ? HANDLING_MIGRATION : said.includes(SIGNALS_MIGRATION) ? SIGNALS_MIGRATION : null
        return {
          ...none,
          kind: 'unprovisioned',
          title: 'Signal handling not provisioned here',
          message: migration
            ? `A table this act needs does not exist in this database: migration ${migration} has not been applied. Nothing was changed.`
            : `The API answered 503 without naming a migration (${errorSummary(error) ?? 'no reason given'}). It is unknown whether anything was recorded.`,
        }
      }
      return { ...none, kind: 'failed', title: `The API failed (${isApiError(error) ? error.status : 500})`, message: RETRY_IS_SAFE }
    }
    case 'network':
      return { ...none, kind: 'failed', title: 'Could not reach the API', message: RETRY_IS_SAFE }
    default:
      return { ...none, kind: 'failed', title: 'The act failed', message: `${errorSummary(error) ?? 'No reason was given.'} ${RETRY_IS_SAFE}` }
  }
}

const RETRY_IS_SAFE =
  'It is unknown whether it was recorded. Retrying is safe: the same act, repeated, changes nothing.'

/** The retry a HandlingChanged refusal offers, by verb. "Take it over" only where that is what it does. */
export function takeOverLabel(verb: SignalHandlingVerb): string {
  switch (verb) {
    case 'acknowledge': return 'Take it over'
    case 'assign': return 'Assign it anyway'
    case 'release': return 'Release it anyway'
  }
}

/** The confirmation before re-sending over somebody else's act. */
export function takeOverConfirmText(verb: SignalHandlingVerb, current: SignalHandling | null): string {
  const now = current ? describeHandling(current) : 'somebody acted on it'
  switch (verb) {
    case 'acknowledge': return `It is now: ${now}. Take it over? It will be yours, and they will see you took it.`
    case 'assign': return `It is now: ${now}. Assign it anyway, replacing that?`
    case 'release': return `It is now: ${now}. Release it anyway, so nobody has it?`
  }
}

// ---------------------------------------------------------------------------
// The toast
// ---------------------------------------------------------------------------

export interface HandlingToast {
  title: string
  message: string
  color: string
}

/** What to say once the server answered. A repeat is not a success: nothing was written or audited. */
export function handlingToast(verb: SignalHandlingVerb, result: SignalHandlingResult): HandlingToast {
  if (!result.changed) {
    return { title: 'Nothing changed', message: `It was already so: ${describeHandling(result.handling)}.`, color: 'gray' }
  }
  switch (verb) {
    case 'acknowledge':
      return {
        title: 'You have this signal',
        message: 'It stays open and counted until the detector sees the condition clear.',
        color: 'blue',
      }
    case 'assign':
      return {
        title: `Assigned to ${served(result.handling.ownerName) ?? UNNAMED_PERSON}`,
        message: `${ASSIGNEE_NOT_NOTIFIED} It stays open and counted.`,
        color: 'blue',
      }
    case 'release':
      return { title: 'Released', message: 'Nobody has this signal now. It stays open and counted.', color: 'blue' }
  }
}

// ---------------------------------------------------------------------------
// The assign dialog's list
// ---------------------------------------------------------------------------

export interface AssigneeRow {
  userId: string
  name: string
  lastActed: string
}

export type AssigneeView =
  | { kind: 'loading' }
  | { kind: 'restricted' }
  | { kind: 'error'; title: string; message: string }
  | { kind: 'empty'; title: string; message: string }
  | { kind: 'ready'; rows: AssigneeRow[]; caption: string | null; windowDays: number }

/**
 * The assign dialog's four states — loading, failed (a 403 is the restricted state, everything else is worded by
 * status), empty ("nobody Observatory can name") and ready — kept distinct, so an empty list is never a failed one.
 * The caller is never in `items` (the server excludes them); a row the server served without a user id is dropped,
 * because the id is the only thing an assignment can send.
 */
export function assigneeView(q: QueryLike<SignalAssigneeList>, entraConfigured = true): AssigneeView {
  if (q.isError) {
    if (errorKind(q.error) === 'forbidden') return { kind: 'restricted' }
    const signIn = signInRequiredText(q.error, entraConfigured)
    if (signIn) return { kind: 'error', title: 'Could not list who can be named', message: signIn }
    switch (errorKind(q.error)) {
      case 'network':
        return { kind: 'error', title: 'Could not reach the API', message: 'The list of people did not load. Nothing was changed.' }
      case 'server':
        return { kind: 'error', title: 'The API failed', message: `${errorSummary(q.error) ?? 'No reason was given.'} Nothing was changed.` }
      default:
        return { kind: 'error', title: 'Could not list who can be named', message: `${errorSummary(q.error) ?? 'The request failed.'} Nothing was changed.` }
    }
  }
  if (q.isLoading || q.data === undefined) return { kind: 'loading' }

  const data = q.data
  const rows = (data.items ?? [])
    .filter((a) => a.userId?.trim())
    .map((a) => ({ userId: a.userId, name: served(a.name) ?? UNNAMED_PERSON, lastActed: `last acted here ${whenLabel(a.lastActedAt)}` }))
  if (rows.length === 0) {
    return {
      kind: 'empty',
      title: 'Nobody Observatory can name',
      message: `Nobody else has performed an Observatory write in the last ${exactNumber(data.windowDays)} days, so there `
        + 'is nobody to hand this signal to. Anyone who can handle signals can take it themselves with “I have got this”.',
    }
  }
  const total = data.totalCount ?? rows.length
  return {
    kind: 'ready',
    rows,
    caption: total > rows.length ? `Showing ${exactNumber(rows.length)} of ${exactNumber(total)}, most recently active first.` : null,
    windowDays: data.windowDays,
  }
}

// ---------------------------------------------------------------------------
// The Inbox's count line
// ---------------------------------------------------------------------------

/**
 * The handling parts of the count line — "3 acknowledged", "2 assigned", "1 changed since handled" — from the
 * summary, which counts them over the same open rows as its open count. They are said only where they describe the
 * listed rows: the unfiltered open view (all open signals), or the open view narrowed to Critical alone (the critical
 * counts). Anywhere else they would be numbers about a different set, so nothing is said. Never a subtraction.
 */
export function handlingCountParts(summary: SignalSummary, filters: InboxFilters): string[] {
  const openOnly = filters.state === 'open' && filters.ruleId === null && filters.subjectKind === null
    && !filters.unconfirmedOnly && filters.handling === null
  if (!openOnly || summary.handlingProvisioned !== true) return []

  let acknowledged: number | null | undefined
  let assigned: number | null | undefined
  let changed: number | null | undefined
  if (filters.severity === null) {
    acknowledged = summary.openAcknowledgedCount
    assigned = summary.openAssignedCount
    changed = summary.openChangedSinceHandledCount
  } else if (filters.severity === 'Critical') {
    acknowledged = summary.openCriticalAcknowledgedCount
    assigned = summary.openCriticalAssignedCount
    changed = null
  } else {
    return []
  }

  const parts: string[] = []
  if (acknowledged != null) parts.push(`${exactNumber(acknowledged)} acknowledged`)
  if (assigned != null) parts.push(`${exactNumber(assigned)} assigned`)
  if (changed != null && changed > 0) parts.push(`${exactNumber(changed)} changed since handled`)
  return parts
}

/** "12 open signals · 3 acknowledged · 2 assigned · showing 1–12", worded by the state filter. */
export function inboxCountLine(data: SignalList, filters: InboxFilters, summary: SignalSummary): string {
  const what = filters.state === 'open' ? 'open signal' : filters.state === 'resolved' ? 'signal no longer open' : 'signal'
  const many = filters.state === 'resolved' ? 'signals no longer open' : `${what}s`
  const parts = [`${exactNumber(data.totalCount)} ${data.totalCount === 1 ? what : many}`, ...handlingCountParts(summary, filters)]
  if (data.items.length > 0) {
    parts.push(`showing ${exactNumber(data.skip + 1)}–${exactNumber(data.skip + data.items.length)}`)
  }
  return parts.join(' · ')
}

/**
 * What the Inbox says about handling when it cannot offer the filter: the table is missing here (with its
 * migration), or the API predates handling (the field is absent). Null when the filter is offered — and when the
 * summary says null, which means the signal store itself is missing and the store's own banner already says so.
 */
export function handlingUnavailableText(summary: SignalSummary): string | null {
  if (summary.handlingProvisioned === true || summary.handlingProvisioned === null) return null
  if (summary.handlingProvisioned === false) {
    return `Who has each signal is not recorded in this database: migration ${HANDLING_MIGRATION} has not been applied, `
      + 'so signals cannot be acknowledged or assigned here, and the handling filter is not offered.'
  }
  return 'This API does not serve signal handling, so the handling filter is not offered.'
}
